import { request } from '../../api';

export interface HealthCheck {
  readonly name: string;
  readonly ok: boolean;
  readonly critical: boolean;
  readonly detail: string;
}

export interface HealthReport {
  readonly status: 'ok' | 'degraded' | 'down';
  readonly checkedAt: string;
  readonly latencyMs: number;
  readonly checks: readonly HealthCheck[];
}

export interface AvailabilityReport {
  readonly from: string;
  readonly to: string;
  readonly samples: number;
  readonly healthy: number;
  readonly ratio: number;
  readonly target: number;
  readonly meetsTarget: boolean;
}

export interface DeviceRow {
  readonly deviceId: string;
  readonly siteId: string;
  readonly siteCode: string;
  readonly kind: 'tablet' | 'pos';
  readonly label: string;
  readonly appVersion: string | null;
  readonly lastSeenAt: string | null;
  readonly queueDepth: number;
  readonly status: 'active' | 'revoked';
  readonly online: boolean;
}

export interface DeviceQueue {
  readonly deviceId: string;
  readonly kind: 'tablet' | 'pos';
  readonly label: string;
  readonly queueDepth: number;
  readonly lastSeenAt: string | null;
  readonly online: boolean;
  readonly pendingOperations: number;
  readonly openConflicts: number;
}

export interface SyncStatus {
  readonly devices: readonly DeviceQueue[];
  readonly totalQueueDepth: number;
  readonly maxQueueDepth: number;
  readonly offlineDevices: number;
  readonly openConflicts: number;
}

export interface AlertRule {
  readonly id: number;
  readonly metric: string;
  readonly comparator: 'gt' | 'lt' | 'eq';
  readonly threshold: number;
  readonly severity: 'info' | 'warning' | 'critical';
  readonly enabled: boolean;
  readonly description: string;
}

export interface OpenAlert {
  readonly id: string;
  readonly metric: string;
  readonly scope: string;
  readonly observed: number;
  readonly threshold: number;
  readonly severity: string;
  readonly raisedAt: string;
}

export interface ReleaseRow {
  readonly id: string;
  readonly version: string;
  readonly channel: 'production' | 'staging';
  readonly status: 'running' | 'completed' | 'failed' | 'rolling_back' | 'rolled_back';
  readonly migrationBefore: number;
  readonly migrationAfter: number | null;
  readonly migrationsApplied?: readonly number[];
  readonly imageTag?: string | null;
  readonly imageBefore?: string | null;
  readonly windowOverride?: string | null;
  readonly rollbackError?: string | null;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  readonly notes: string;
}

export interface RollbackPlan {
  readonly from: number;
  readonly to: number;
  readonly steps: number;
  readonly versions: readonly number[];
  readonly imageFrom: string | null;
  readonly imageTo: string | null;
}

export interface RollbackRequest {
  readonly release: ReleaseRow;
  readonly plan: RollbackPlan;
  /** Всегда false: откат выполняет сервер командой `command`, а не браузер. */
  readonly executed: boolean;
  readonly command: string;
}

export interface BackupRow {
  readonly id: string;
  readonly kind: string;
  readonly status: string;
  readonly location: string;
  readonly sizeBytes: number | null;
  readonly offsite: boolean;
  readonly startedAt: string;
  readonly finishedAt: string | null;
}

export interface DrillRow {
  readonly id: string;
  readonly backupId: string;
  readonly status: 'running' | 'passed' | 'failed';
  readonly startedAt: string;
  readonly finishedAt: string | null;
  readonly rtoSeconds: number | null;
  readonly mismatches: number;
  readonly tablesChecked: number;
}

export interface ExternalService {
  readonly code: string;
  readonly name: string;
  readonly category: string;
  readonly vendor: string;
  readonly processesPii: boolean;
  readonly hostingCountry: string | null;
  readonly verdict: 'pending' | 'approved' | 'rejected';
  readonly evidence: string;
  readonly checkedAt: string | null;
}

export interface ResidencyReport {
  readonly services: readonly ExternalService[];
  readonly total: number;
  readonly pending: number;
  readonly approved: number;
  readonly rejected: number;
  readonly compliant: boolean;
}

export async function fetchHealth(): Promise<HealthReport> {
  return request<HealthReport>('GET', '/api/v1/ops/health');
}

export async function fetchAvailability(from: string, to: string): Promise<AvailabilityReport> {
  const query = new URLSearchParams({ from, to });
  return request<AvailabilityReport>('GET', `/api/v1/ops/availability?${query.toString()}`);
}

export async function fetchDevices(siteId?: string): Promise<readonly DeviceRow[]> {
  const query = siteId ? `?site_id=${encodeURIComponent(siteId)}` : '';
  const data = await request<{ devices: readonly DeviceRow[] }>(
    'GET',
    `/api/v1/ops/devices${query}`,
  );
  return data.devices;
}

export async function revokeDevice(deviceId: string, reason: string): Promise<DeviceRow> {
  const data = await request<{ device: DeviceRow }>(
    'POST',
    `/api/v1/ops/devices/${encodeURIComponent(deviceId)}/revoke`,
    { reason },
  );
  return data.device;
}

export async function fetchSyncStatus(siteId?: string): Promise<SyncStatus> {
  const query = siteId ? `?site_id=${encodeURIComponent(siteId)}` : '';
  return request<SyncStatus>('GET', `/api/v1/ops/sync-status${query}`);
}

export async function fetchAlerts(): Promise<{
  readonly open: readonly OpenAlert[];
  readonly rules: readonly AlertRule[];
}> {
  return request('GET', '/api/v1/ops/alerts');
}

export async function saveRule(patch: {
  readonly metric: string;
  readonly threshold?: number;
  readonly enabled?: boolean;
}): Promise<AlertRule> {
  const data = await request<{ rule: AlertRule }>('POST', '/api/v1/ops/alerts/rules', patch);
  return data.rule;
}

export async function fetchReleases(): Promise<readonly ReleaseRow[]> {
  const data = await request<{ releases: readonly ReleaseRow[] }>('GET', '/api/v1/ops/releases');
  return data.releases;
}

export async function rollbackRelease(id: string): Promise<RollbackRequest> {
  return request<RollbackRequest>('POST', `/api/v1/ops/releases/${encodeURIComponent(id)}/rollback`);
}

export async function fetchBackups(): Promise<{
  readonly backups: readonly BackupRow[];
  readonly drills: readonly DrillRow[];
  /** Доступны ли `pg_dump`/`pg_restore`: без них ни копия, ни учение невозможны. */
  readonly toolsAvailable: boolean;
  readonly toolPrefix: string;
}> {
  return request('GET', '/api/v1/ops/backups');
}

/**
 * Снять копию вручную.
 *
 * Вид `manual` отличает её от ежедневной по расписанию и от предрелизной: в
 * журнале должно быть видно, что копию сняли руками и зачем.
 */
export async function runBackup(): Promise<BackupRow> {
  const data = await request<{ backup: BackupRow }>('POST', '/api/v1/ops/backups', {
    kind: 'manual',
  });
  return data.backup;
}

export async function runDrill(backupId?: string): Promise<DrillRow> {
  const data = await request<{ drill: DrillRow }>(
    'POST',
    '/api/v1/ops/backups/drill',
    backupId ? { backupId } : {},
  );
  return data.drill;
}

export async function fetchResidency(): Promise<ResidencyReport> {
  return request<ResidencyReport>('GET', '/api/v1/ops/residency');
}

export async function decideResidency(
  code: string,
  body: {
    readonly verdict: 'pending' | 'approved' | 'rejected';
    readonly hostingCountry?: string | null;
    readonly evidence: string;
  },
): Promise<ExternalService> {
  const data = await request<{ service: ExternalService }>(
    'POST',
    `/api/v1/ops/residency/${encodeURIComponent(code)}`,
    body,
  );
  return data.service;
}

/* ------------------------------------------------- постановка на учёт */

export type DeviceKind = 'tablet' | 'pos';

export interface EnrolledDevice {
  readonly deviceId: string;
  readonly siteId: string;
  readonly label: string;
  /** Показывается ровно один раз: в базе лежит только хеш. */
  readonly token: string;
}

/**
 * Поставить устройство на учёт.
 *
 * Единой ручки на это нет и заводить её незачем: устройство принадлежит своему
 * модулю — планшет объявлен spec-3, касса spec-7, — и каждый ведёт свой
 * инвентарь со своими правилами отзыва. Раздел «Эксплуатация» здесь выступает
 * как единая точка входа для человека, а не как ещё один слой на сервере:
 * он просто зовёт маршрут того модуля, чьё устройство ставят.
 *
 * До этого экрана планшет ставился на учёт только curl-ом: маршрут
 * `/api/v1/tablet/devices/enroll` существовал с самого начала, а показывать его
 * было негде.
 */
export function enrollPath(kind: DeviceKind): string {
  return kind === 'tablet' ? '/api/v1/tablet/devices/enroll' : '/api/v1/pos/devices';
}

/** Куда идти на самом устройстве после ввода токена. */
export function deviceUiPath(kind: DeviceKind): string {
  return kind === 'tablet' ? '/api/v1/tablet/ui' : '/api/v1/pos/ui';
}

export async function enrollDevice(input: {
  kind: DeviceKind;
  siteId: string;
  label: string;
}): Promise<EnrolledDevice> {
  const res = await request<{
    device: { deviceId: string; siteId: string; label: string };
    token: string;
  }>('POST', enrollPath(input.kind), { siteId: input.siteId, label: input.label });
  return { ...res.device, token: res.token };
}
